import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  ArtistSummary, ArtworkEnquiryService, EnquiryArtwork, EnquiryDraft,
} from '../../Services/shared/artwork-enquiry.service';

/**
 * The artwork detail panel, and the enquiry form it opens.
 *
 * <b>Why this is one component and not markup in each page.</b> A visitor who clicks a piece wants
 * to see it and ask about it — not to be sent somewhere else. That interaction is now needed by the
 * published artist site and by the promotional mosaic, and will be needed anywhere else artwork is
 * shown to the public. Written twice it drifts, and the fields most likely to drift are the ones
 * that matter: the price, the sold badge, and whether the piece reference reaches the artist.
 *
 * <b>Self-contained by design.</b> It carries its own overlay styling rather than relying on an
 * ancestor stylesheet, because a caller outside the website builder has none. The modals were
 * already look-independent — flat selectors over a white card floating above the page canvas — so
 * nothing was lost by moving them.
 *
 * <b>Driven by methods, not inputs.</b> A host holds a template reference and calls
 * `openDetail(work)` / `openEnquiry(work)`. That keeps the host free of modal state it does not
 * otherwise need — the website builder's grid simply calls `panel.openDetail(w)` on a card click.
 */
@Component({
  selector: 'ds-artwork-enquiry',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './ds-artwork-enquiry.component.html',
  styleUrls: ['./ds-artwork-enquiry.component.scss'],
})
export class DsArtworkEnquiryComponent {

  /** Identifies the recipient — the published site's slug. Sent with every enquiry. */
  @Input() identifier = '';

  /** Currency the price is shown in. Display only; the server is authoritative on money. */
  @Input() currency = 'USD';

  /**
   * Where the mailto fallback goes when sending fails, and the flag that decides whether an
   * enquiry is offered at all. No address, no enquiry: the button is hidden rather than offered
   * and then failing.
   */
  @Input() contactEmail: string | null = null;

  /** Names the thing being enquired about in the mail subject — usually the site or page title. */
  @Input() contextTitle = '';

  /** Fired after a successful send, so a host can react (analytics, a toast of its own). */
  @Output() sent = new EventEmitter<EnquiryArtwork | null>();

  /**
   * Offer an "Artist" tab beside the piece.
   *
   * <b>Off by default, and that is not timidity.</b> On an artist's own website the whole page
   * already answers "who made this", so a tab introducing them is noise. On a wall of many artists
   * it is the first thing a visitor wants. The caller knows which it is; this component does not.
   */
  @Input() showArtistTab = false;

  public detailTab: 'work' | 'artist' = 'work';
  public artist: ArtistSummary | null = null;
  public artistLoading = false;
  /** Resolved and found nothing — so the tab hides rather than opening on an empty panel. */
  public artistUnavailable = false;

  public detailWork: EnquiryArtwork | null = null;
  public enquiryOpen = false;
  public enquiryWork: EnquiryArtwork | null = null;

  public draft: EnquiryDraft = { name: '', email: '', message: '' };
  public sending = false;
  public wasSent = false;
  public error = '';

  constructor(private enquiries: ArtworkEnquiryService) {}

  /**
   * Whether to offer an enquiry at all.
   *
   * Left null, this falls back to "is there an address" — which is right for the published site,
   * where the contact section IS the enquiry route and offering a button without one would fail.
   *
   * A host may override it. The SERVER resolves the recipient from the identifier and the piece,
   * so an address is only needed for the mailto fallback, not to send. Conflating the two would
   * mean any caller that does not happen to know the address cannot enquire at all.
   */
  @Input() enquiryAvailable: boolean | null = null;

  public get canEnquire(): boolean {
    return this.enquiryAvailable !== null ? this.enquiryAvailable : !!this.contactEmail;
  }

  public openDetail(work: EnquiryArtwork): void {
    this.detailWork = work;
    // Always open on the piece — they clicked a picture, not a biography. And reset, because the
    // panel is reused across pieces and a previous artist lingering here would mislabel this one.
    this.detailTab = 'work';
    this.artist = null;
    this.artistUnavailable = false;

    // Fetch the artist NOW, in the background, while the visitor reads the piece.
    //
    // This deliberately replaces an earlier "load only when the tab is clicked" rule. That rule was
    // protecting the wrong thing: the request never blocked anything — the piece renders from the
    // manifest either way — and deferring it simply moved the wait to the moment someone asked for
    // it. A spinner on click is a worse cost than a request nobody notices.
    this.prefetchArtist(work);
  }

  public closeDetail(): void {
    this.detailWork = null;
    this.detailTab = 'work';
  }

  /**
   * Switch tabs. The artist is normally already here, fetched when the panel opened.
   *
   * The load is still guarded rather than assumed: a piece opened and clicked immediately, or a slow
   * connection, can outrun the prefetch — in which case this is the fallback that starts it.
   */
  public showTab(tab: 'work' | 'artist'): void {
    this.detailTab = tab;
    if (tab === 'artist') { this.loadArtist(this.detailWork); }
  }

  /**
   * Warm the artist while the visitor reads the piece.
   *
   * Reads the slug from the WORK, never from the `identifier` input: that input is bound from the
   * work being opened, so at this point in `openDetail()` it still holds the previous artist. The
   * service caches per identifier, so a visitor working through several pieces by one artist causes
   * exactly one request.
   */
  private prefetchArtist(work: EnquiryArtwork): void {
    if (!this.showArtistTab) { return; }
    this.loadArtist(work);
  }

  private loadArtist(work: EnquiryArtwork | null): void {
    if (this.artist || this.artistLoading) { return; }

    const identifier = (work && work.artistIdentifier) || this.identifier;
    if (!identifier) { this.artistUnavailable = true; return; }

    this.artistLoading = true;
    this.enquiries.artistSummary(identifier).subscribe((summary) => {
      this.artistLoading = false;
      this.artist = summary;
      this.artistUnavailable = !summary;
      // Nothing to show is not an error, but it is not a tab either — go back to the work. Only
      // relevant if they beat the prefetch to it; otherwise the tab has already hidden itself.
      if (!summary && this.detailTab === 'artist') { this.detailTab = 'work'; }
    });
  }

  /** `null` raises a general enquiry — from a contact button rather than a piece. */
  public openEnquiry(work: EnquiryArtwork | null): void {
    this.enquiryWork = work;
    this.wasSent = false;
    this.error = '';
    this.enquiryOpen = true;
  }

  public closeEnquiry(): void {
    this.enquiryOpen = false;
    this.enquiryWork = null;
  }

  /** From the detail panel: close it and ask about the piece it was showing. */
  public enquireAboutDetail(): void {
    const work = this.detailWork;
    this.closeDetail();
    this.openEnquiry(work);
  }

  public statusOf(work: EnquiryArtwork): string {
    return this.enquiries.statusOf(work);
  }

  public submit(event: Event): void {
    event.preventDefault();
    if (this.sending || !this.enquiries.isSendable(this.draft)) { return; }

    this.sending = true;
    this.error = '';

    this.enquiries.send(this.identifier, this.draft, this.enquiryWork).subscribe((outcome) => {
      this.sending = false;

      if (!outcome.ok) {
        // The typed message stays on screen — losing it is worse than the failure.
        this.error = outcome.error;
        return;
      }

      this.wasSent = true;
      this.sent.emit(this.enquiryWork);
      this.draft = { name: '', email: '', message: '' };
    });
  }

  /** Offered only after a failure, so a visitor never loses what they typed. */
  public mailtoFallback(): string {
    return this.enquiries.mailto(
      this.contactEmail, this.contextTitle, this.draft, this.enquiryWork, this.currency);
  }
}
